// @ts-nocheck
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { generateClient } from "aws-amplify/api";
import {
  listGeoPoints,
  geoPointsByAssetGroupId,
  getGeoPoint,
} from "../graphql/queries";
import {
  createGeoPoint,
  deleteGeoPoint,
  updateGeoPoint,
  createProcessedModel,
  updatePerformanceCurve,
} from "../graphql/mutations";
import { schemaMatch } from "../util/utils";
import { minRainfallDataYear, maxRainfallDataYear } from "../util/constants";
import { geoPointsByAssetGroupIdCustom, fetchGroupsAndNestedAssetsCustom } from "../graphql/customQueries";
import { partialGraphqlData } from "../utils/graphqlPartial";
import { listAllPages } from "../utils/graphqlPagination";
import {
  createGeoPointCustom,
  createProcessedModelCustom,
  deleteGeoPointCustom,
  updateGeoPointCustom
} from "../graphql/customMutations";

const client = generateClient();

// Groups a page when each carries its assets: the nested lists make pages heavy, so keep them modest
const NESTED_GROUP_PAGE_SIZE = 100;
export const assetSchema = [
  "name",
  "lat",
  "lng",
  "description",
  "assetGroupId",
  "value",
  "id",
  "elevation",
  "assetModels",
  "processedModels",
  "owner",
  "geojson",
  "imageUrl",
  "displayedChannels",
];

//CRAIG put mutation loading state
const initialState = {
  data: [],
  loading: "idle",
  error: null,
  hoveredAsset: null,
  selectedAsset: null,
  selectedAssetProximity: 0,
  stationYrlyCountFilter: 1,
  stationYearRange: [minRainfallDataYear, maxRainfallDataYear], //CRAIG CHANGE TO CONSTANTS
  proximityStations: [],
  filteredProximityStationIds: [],
  assetLayerVisible: true,
  freqAnalyses: {},
  dataGroups: {},
  activeProcessedModels: {},
};

export const postProcessedModel = createAsyncThunk(
  "assets/postProcessedModel",
  async (input, thunkAPI) => {
    try {
      console.log("input", input);
      const { data } = await client.graphql({
        query: createProcessedModelCustom,
        variables: { input },
      });
      console.log("data", data);

      return { data };
    } catch (error) {
      console.log("error", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// export const fetchAssetsByGroupId = createAsyncThunk(
//   "assets/fetchAssetsByGroupId",
//   async (assetGroupId, thunkAPI) => {
//     try {
//       const { data } = await client.graphql({
//         query: geoPointsByAssetGroupIdCustom,
//         variables: {
//           assetGroupId,
//           limit:100,
//           sortDirection: "ASC"
//         },
//       });
//       return { assetGroupId, assets: data.geoPointsByAssetGroupIdAndName.items };
//     } catch (error) {
//       console.log("error", error);
//       return thunkAPI.rejectWithValue(error.message);
//     }
//   }
// );

export const fetchAssetsByGroupId = createAsyncThunk(
  "assets/fetchAssetsByGroupId",
  async (assetGroupId: string, thunkAPI) => {
    try {
      let allItems: GeoPoint[] = [];
      let nextToken: string | null = null;

      do {
        type Page = { geoPointsByAssetGroupIdAndName: { items: GeoPoint[]; nextToken: string | null } };
        let data: Page;
        try {
          ({ data } = await client.graphql<Page>({
            query: geoPointsByAssetGroupIdCustom,
            variables: {
              assetGroupId,
              limit: 100,
              sortDirection: "ASC",
              nextToken,              // pass the token from last page
            },
          }));
        } catch (error) {
          // One unreadable nested record (e.g. a forecast outside the viewer's org) must not hide every asset
          data = partialGraphqlData<Page>(error, "geoPointsByAssetGroupIdAndName", "fetchAssetsByGroupId");
        }

        const page = data.geoPointsByAssetGroupIdAndName;
        // A partial result can leave an unreadable row as null
        allItems = allItems.concat(page.items.filter(Boolean));
        nextToken = page.nextToken;  // will be null when you’re done
      } while (nextToken);

      return { assetGroupId, assets: allItems };
    } catch (error: any) {
      console.error("error", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// This selects assets Groups and then the assets within them for assets/dataGroups
// TODO: REFACTOR - Store group names in state for better UX
// Current: Only stores { [groupId]: [assets] } - group names are lost
// Future: Change to { [groupId]: { name: 'Group Name', description: '...', assets: [...] } }
// This will allow components to display group names without separate queries
// Related: AssetBoundaryField in SchemaFieldRenderer currently makes its own query
// After refactoring this, update AssetBoundaryField to use Redux state instead
export const fetchGroupsAndNestedAssets = createAsyncThunk(
  "assets/fetchGroupsAndNestedAssets",
  async (thunkAPI) => {
    try {
      // Every page of groups; a partial page (an unreadable nested record) keeps what it could read
      const { items } = await listAllPages<any>(
        fetchGroupsAndNestedAssetsCustom,
        "listAssetGroups",
        { limit: NESTED_GROUP_PAGE_SIZE },
        { label: "fetchGroupsAndNestedAssets", allowPartial: true }
      );

      // A partial result can leave an unreadable asset as null
      const resp = items.reduce((acc, curr) => {
        return {
          [curr.id]: (curr.assets?.items ?? []).filter(Boolean),
          ...acc
        }
      },{})
      return resp
    } catch (error) {
      console.log("error", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchAssetById = createAsyncThunk(
  "assets/fetchAssetById",
  async (id, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: getGeoPoint,
        variables: {
          input: { id },
        },
      });
      return data.getGeoPoint;
    } catch (error) {
      console.log("fetchAssetById error", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchAssets = createAsyncThunk(
  "assets/fetchAllUserAssets",
  async (_, thunkAPI) => {
    try {
      const { data } = await client.graphql({ query: listGeoPoints });
      return data.listGeoPoints.items;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const postAsset = createAsyncThunk(
  "assets/PostAsset",
  async (input, thunkAPI) => {
    delete input.hasGeojson
    try {
      const { data } = await client.graphql({
        query: createGeoPointCustom,
        variables: { input },
      });
      return {
        assetGroupId: data.createGeoPoint.assetGroupId,
        asset: data.createGeoPoint,
      };
      // return data.createGeoPoint.assetGroupId;
    } catch (error) {
      console.log("error", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteAsset = createAsyncThunk(
  "assets/DeleteAsset",
  async (input, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: deleteGeoPointCustom,
        variables: { input },
      });

      return {
        assetGroupId: data.deleteGeoPoint.assetGroupId,
        asset: data.deleteGeoPoint,
      };

      // return data.deleteGeoPoint;
    } catch (error) {
      console.log("DeleteError", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const editAsset = createAsyncThunk(
  "assets/editAsset",
  async (input, thunkAPI) => {
    try {
      // Check if asset has organizationId and windCurveId
      const { organizationId, windCurveId } = input;
      
      // First update the asset
      const { data } = await client.graphql({
        query: updateGeoPointCustom,
        variables: { input },
      });
      
      // If asset has both organizationId and windCurveId, update the performance curve
      if (organizationId && windCurveId) {
        try {
          // Get the current performance curve data
          const state = thunkAPI.getState();
          const performanceCurve = state.pCurve?.data?.[windCurveId];
          
          if (performanceCurve) {
            // Get current organizationIds array or initialize an empty one
            const currentOrgIds = performanceCurve.organizationIds || [];
            
            // Check if organizationId is already in the list
            if (!currentOrgIds.includes(organizationId)) {
              // Add the organizationId to the list
              const updatedOrgIds = [...currentOrgIds, organizationId];
              
              // Update the performance curve
              await client.graphql({
                query: updatePerformanceCurve,
                variables: { 
                  input: { 
                    id: windCurveId,
                    organizationIds: updatedOrgIds
                  } 
                },
              });
            }
          } else {
            console.log(`Performance curve ${windCurveId} not found in state`);
          }
        } catch (pcurveError) {
          console.log("Error updating performance curve:", pcurveError);
          // Don't reject the main operation if this fails
        }
      }
      
      return data.updateGeoPoint;
    } catch (error) {
      console.log("error", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const AssetsSlice = createSlice({
  name: "assets",
  initialState,
  reducers: {
    addAssetsToDataGroups: (state, action) => {
      state.dataGroups[action.payload.assetGroupId] = action.payload.assets
    },
    setSelectedAsset: (state, action) => {
      state.selectedAsset = action.payload;
    },
    setHoveredAsset: (state, action) => {
      state.hoveredAsset = action.payload;
    },
    setSelectedAssetProximity: (state, action) => {
      state.selectedAssetProximity = action.payload;
    },
    setProximityStations: (state, action) => {
      state.proximityStations = action.payload;
    },
    setStationYrlyCountFilter: (state, action) => {
      state.stationYrlyCountFilter = action.payload;
    },
    setStationYearRange: (state, action) => {
      state.stationYearRange = action.payload;
    },
    setFilteredStationIds: (state, action) => {
      state.filteredProximityStationIds = action.payload;
    },
    setActiveProcessedModel: (state, action) => {
      const { assetId, routeShort } = action.payload;
      state.activeProcessedModels[assetId] = routeShort;
    },
    clearActiveProcessedModel: (state, action) => {
      delete state.activeProcessedModels[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(postProcessedModel.pending, (state, action) => {})
      .addCase(postProcessedModel.fulfilled, (state, action) => {
        console.log("added new processedModel");
      })
      .addCase(postProcessedModel.rejected, (state, action) => {})
      .addCase(fetchAssetById.pending, (state, action) => {})
      .addCase(fetchAssetById.fulfilled, (state, action) => {
        if (!state.dataGroups[action.payload.assetGroupId]) {
          state.dataGroups[action.payload.assetGroupId] = [action.payload];
        }
      })
      .addCase(fetchAssetById.rejected, (state, action) => {})
      .addCase(editAsset.pending, (state, action) => {
        // state.dataGroups[action.payload.assetGroupId] = state.dataGroups[action.payload.assetGroupId].map(asset => {
        //   if(asset.id === action.payload.id) {
        //     return action.payload
        //   }
        //   return asset
        // })
      })
      .addCase(editAsset.fulfilled, (state, action) => {
        // Check if the asset group exists and has items; if so, proceed with mapping
        state.dataGroups[action.payload.assetGroupId] = state.dataGroups[
          action.payload.assetGroupId
        ]?.length
          ? state.dataGroups[action.payload.assetGroupId].map((asset) => {
              if (asset.id === action.payload.id) {
                return action.payload;
              }
              return asset;
            })
          : state.dataGroups[action.payload.assetGroupId]; // Skip mapping if no items
      })
      .addCase(editAsset.rejected, (state, action) => {})
      .addCase(fetchAssetsByGroupId.pending, (state) => {
        // state.loading = "pending";
        // state.error = null;
      })
      .addCase(fetchAssetsByGroupId.fulfilled, (state, action) => {
        state.loading = "success";
        state.dataGroups[action.payload.assetGroupId] = action.payload.assets;
      })
      .addCase(fetchAssetsByGroupId.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message;
      })
      .addCase(fetchGroupsAndNestedAssets.pending, (state) => {
        // state.loading = "pending";
        // state.error = null;
      })
      .addCase(fetchGroupsAndNestedAssets.fulfilled, (state, action) => {
        state.loading = "success";
        state.dataGroups = action.payload;
      })
      .addCase(fetchGroupsAndNestedAssets.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message;
      })
      .addCase(fetchAssets.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(fetchAssets.fulfilled, (state, action) => {
        state.loading = "success";
        state.data = action.payload;
      })
      .addCase(fetchAssets.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message;
      })
      .addCase(postAsset.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(postAsset.fulfilled, (state, action) => {
        state.loading = "success";
        state.dataGroups[action.payload.assetGroupId].push(
          action.payload.asset
        );
      })
      .addCase(postAsset.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message;
      })
      .addCase(deleteAsset.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteAsset.fulfilled, (state, action) => {
        state.loading = "success";

        state.dataGroups[action.payload.assetGroupId] = state.dataGroups[
          action.payload.assetGroupId
        ].filter((asset) => asset.id !== action.payload.asset.id);
      })
      .addCase(deleteAsset.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message;
      });
  },
});

// Total assets the user has across every group (groups themselves are not capped).
// Used by the membership-tier asset limit checks in the creation flows.
// KNOWN GAP (fails open): counts only dataGroups currently in Redux, so a user who hasn't loaded every group 
// (e.g. Home → New Asset before visiting other groups) can undercount and bypass the cap; 
// fetchGroupsAndNestedAssets on boot mitigates but doesn't close this — remove once backend enforcement lands.
export const selectTotalAssetCount = (state): number =>
  Object.values(state.assets.dataGroups || {}).reduce(
    (total, assets) => total + (Array.isArray(assets) ? assets.length : 0),
    0
  );

export const {
  setSelectedAsset,
  setHoveredAsset,
  setSelectedAssetProximity,
  setProximityStations,
  setStationYrlyCountFilter,
  setStationYearRange,
  setFilteredStationIds,
  addAssetsToDataGroups,
  setActiveProcessedModel,
  clearActiveProcessedModel,
} = AssetsSlice.actions; // for non async
export default AssetsSlice.reducer;
